<script setup lang="ts">
/**
 * The ruler of a document page (Write): centimetre ticks and numbers across
 * the printable length, shaded margins, the paragraph's tab stops and the two
 * margin handles.
 *
 * `orientation="vertical"` is the ruler beside the page: the page height
 * (`lengthMm`) with its top and bottom margins shaded and numbered from the
 * top margin. It only shows the geometry — no tab stops, no handles.
 *
 * - Double-click on the ruler asks for a tab stop of the kind chosen in the
 *   corner select (`tabKind`) at that place (`add-tab-stop`, millimetres from
 *   the left margin, only inside the printable width).
 * - A click on a tab stop asks to switch its kind (`cycle-tab-stop`), a
 *   right-click to remove it (`remove-tab-stop`).
 * - A margin handle is dragged with the pointer (`margin-drag-start`, then
 *   `margin-drag` with the new margin in half millimetres, `margin-drag-end`)
 *   or moved with ArrowLeft / ArrowRight by a millimetre (`margin-step`, the
 *   change of that margin).
 *
 * Geometry in millimetres; `pxPerMm` is the page's scale on screen (zoom
 * included). The host owns the document: it applies, clamps and records what
 * the ruler asks for. In the shared document workspace (`data-office-view` on
 * an ancestor) the ruler takes the workspace look.
 */
import { computed, ref } from 'vue';

export type NxOfficeRulerTabAlign = 'left' | 'center' | 'right' | 'decimal';
export type NxOfficeRulerSide = 'left' | 'right';
export type NxOfficeRulerOrientation = 'horizontal' | 'vertical';
export interface NxOfficeRulerTabStop {
  positionMm: number;
  align: NxOfficeRulerTabAlign;
}

const props = withDefaults(
  defineProps<{
    /** Page width in millimetres; the page height on a vertical ruler. */
    lengthMm: number;
    /** Screen pixels per millimetre (zoom included). */
    pxPerMm: number;
    orientation?: NxOfficeRulerOrientation;
    /** The margins of a horizontal ruler. */
    marginLeftMm?: number;
    marginRightMm?: number;
    /** The margins of a vertical ruler. */
    marginTopMm?: number;
    marginBottomMm?: number;
    /** The current paragraph's tab stops, millimetres from the left margin. */
    tabStops?: readonly NxOfficeRulerTabStop[];
    /** Accessible name of the ruler (default: horizontal or vertical ruler). */
    label?: string;
    /** Title of the ruler: how to add a tab stop. */
    hint?: string;
    /** Title and accessible name of the tab-kind select. */
    tabKindLabel?: string;
    leftMarginLabel?: string;
    rightMarginLabel?: string;
    /** Title of a margin handle („Linker Seitenrand: 25 mm“). */
    marginTitle?: (side: NxOfficeRulerSide, mm: number) => string;
    /** Title of a tab stop (what a click and a right-click do). */
    tabStopTitle?: (stop: NxOfficeRulerTabStop) => string;
    /** Accessible name of a tab stop. */
    tabStopLabel?: (stop: NxOfficeRulerTabStop) => string;
    /** Stays at the top of its scroll container while the page scrolls. */
    sticky?: boolean;
    /** The one-character mark of each kind, in the select and on the stop. */
    tabKindMarks?: Readonly<Record<NxOfficeRulerTabAlign, string>>;
  }>(),
  {
    orientation: 'horizontal',
    marginLeftMm: 0,
    marginRightMm: 0,
    marginTopMm: 0,
    marginBottomMm: 0,
    tabStops: () => [],
    label: undefined,
    hint: 'Double-click to add a tab stop',
    tabKindLabel: 'New tab stop alignment',
    leftMarginLabel: 'Left margin',
    rightMarginLabel: 'Right margin',
    marginTitle: undefined,
    tabStopTitle: undefined,
    tabStopLabel: undefined,
    sticky: false,
    tabKindMarks: () => ({ left: 'L', center: 'C', right: 'R', decimal: '.' }),
  }
);

const tabKind = defineModel<NxOfficeRulerTabAlign>('tabKind', { default: 'left' });

const emit = defineEmits<{
  'add-tab-stop': [positionMm: number];
  'cycle-tab-stop': [positionMm: number];
  'remove-tab-stop': [positionMm: number];
  'margin-drag-start': [side: NxOfficeRulerSide];
  'margin-drag': [side: NxOfficeRulerSide, mm: number];
  'margin-drag-end': [side: NxOfficeRulerSide];
  'margin-step': [side: NxOfficeRulerSide, deltaMm: number];
}>();

const KINDS: readonly NxOfficeRulerTabAlign[] = ['left', 'center', 'right', 'decimal'];

const vertical = computed(() => props.orientation === 'vertical');
const startMm = computed(() => (vertical.value ? props.marginTopMm : props.marginLeftMm));
const endMm = computed(() => (vertical.value ? props.marginBottomMm : props.marginRightMm));
const width = computed(() => props.lengthMm * props.pxPerMm);
const left = computed(() => startMm.value * props.pxPerMm);
const right = computed(() => endMm.value * props.pxPerMm);
const centimetre = computed(() => 10 * props.pxPerMm);
const printableMm = computed(() => Math.max(0, props.lengthMm - startMm.value - endMm.value));
/** Along the ruler: `left` on a horizontal one, `top` on a vertical one. */
const along = computed(() => (vertical.value ? 'top' : 'left'));
/** The ruler's length and a shade's size: width, or height when vertical. */
const extent = computed(() => (vertical.value ? 'height' : 'width'));
const shades = computed(() => [
  { side: vertical.value ? 'top' : 'left', size: left.value },
  { side: vertical.value ? 'bottom' : 'right', size: right.value },
]);
const labels = computed(() =>
  Array.from({ length: Math.floor(printableMm.value / 10) + 1 }, (_, value) => ({
    value,
    x: left.value + value * centimetre.value,
  }))
);
const stops = computed(() =>
  props.tabStops.map((stop) => ({ ...stop, x: left.value + stop.positionMm * props.pxPerMm }))
);
const rulerLabel = computed(
  () => props.label ?? (vertical.value ? 'Vertical ruler' : 'Horizontal ruler')
);
const rulerStyle = computed(() => ({
  [extent.value]: `${width.value}px`,
  '--nxt-office-ruler--centimetre': `${centimetre.value}px`,
  '--nxt-office-ruler--minor': `${centimetre.value / 5}px`,
  // The ticks count from the start margin, like the numbers.
  '--nxt-office-ruler--origin': `${left.value}px`,
}));

function marginOf(side: NxOfficeRulerSide): number {
  return side === 'left' ? props.marginLeftMm : props.marginRightMm;
}
function marginLabel(side: NxOfficeRulerSide): string {
  return side === 'left' ? props.leftMarginLabel : props.rightMarginLabel;
}

function onDoubleClick(event: MouseEvent): void {
  if (vertical.value) return;
  const rect = (event.currentTarget as HTMLElement).getBoundingClientRect();
  const positionMm = (event.clientX - rect.left - left.value) / props.pxPerMm;
  if (positionMm < 1 || positionMm > printableMm.value) return;
  emit('add-tab-stop', positionMm);
}

let drag: { side: NxOfficeRulerSide; pointerId: number; startX: number; startMm: number } | null =
  null;
const dragging = ref<NxOfficeRulerSide | null>(null);

function beginDrag(side: NxOfficeRulerSide, event: PointerEvent): void {
  drag = { side, pointerId: event.pointerId, startX: event.clientX, startMm: marginOf(side) };
  dragging.value = side;
  (event.currentTarget as HTMLElement).setPointerCapture?.(event.pointerId);
  emit('margin-drag-start', side);
}

function moveDrag(event: PointerEvent): void {
  if (!drag || drag.pointerId !== event.pointerId) return;
  // The right margin grows as its handle moves left.
  const direction = drag.side === 'left' ? 1 : -1;
  const deltaMm = ((event.clientX - drag.startX) / props.pxPerMm) * direction;
  emit('margin-drag', drag.side, Math.round((drag.startMm + deltaMm) * 2) / 2);
}

function endDrag(event: PointerEvent): void {
  if (!drag || drag.pointerId !== event.pointerId) return;
  const handle = event.currentTarget as HTMLElement;
  if (handle.hasPointerCapture?.(event.pointerId)) handle.releasePointerCapture(event.pointerId);
  const { side } = drag;
  drag = null;
  dragging.value = null;
  emit('margin-drag-end', side);
}

function onHandleKeydown(side: NxOfficeRulerSide, event: KeyboardEvent): void {
  if (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight') return;
  event.preventDefault();
  event.stopPropagation();
  const direction = event.key === 'ArrowRight' ? 1 : -1;
  emit('margin-step', side, side === 'left' ? direction : -direction);
}
</script>

<template>
  <div
    class="nx-office-ruler"
    :class="{ 'is-sticky': props.sticky, 'is-vertical': vertical }"
    :style="rulerStyle"
    :aria-label="rulerLabel"
    :title="vertical ? undefined : props.hint"
    data-testid="nx-office-ruler"
    @dblclick="onDoubleClick"
  >
    <select
      v-if="!vertical"
      v-model="tabKind"
      class="nx-office-ruler__tab-kind"
      :aria-label="props.tabKindLabel"
      :title="props.tabKindLabel"
      @click.stop
      @dblclick.stop
    >
      <option v-for="kind in KINDS" :key="kind" :value="kind">
        {{ props.tabKindMarks[kind] }}
      </option>
    </select>
    <div class="nx-office-ruler__ticks" aria-hidden="true" />
    <div
      v-for="shade in shades"
      :key="shade.side"
      class="nx-office-ruler__shade"
      :class="`nx-office-ruler__shade--${shade.side}`"
      :style="{ [extent]: `${shade.size}px` }"
      aria-hidden="true"
    />
    <span
      v-for="mark in labels"
      :key="mark.value"
      class="nx-office-ruler__label"
      :style="{ [along]: `${mark.x}px` }"
      aria-hidden="true"
    >
      {{ mark.value }}
    </span>
    <button
      v-for="stop in vertical ? [] : stops"
      :key="`${stop.positionMm}-${stop.align}`"
      type="button"
      class="nx-office-ruler__tab-stop"
      :style="{ left: `${stop.x}px` }"
      :title="props.tabStopTitle?.(stop)"
      :aria-label="props.tabStopLabel?.(stop) ?? `${stop.align} ${stop.positionMm} mm`"
      @click.stop="emit('cycle-tab-stop', stop.positionMm)"
      @dblclick.stop
      @contextmenu.prevent.stop="emit('remove-tab-stop', stop.positionMm)"
    >
      {{ props.tabKindMarks[stop.align] }}
    </button>
    <button
      v-for="side in vertical ? [] : (['left', 'right'] as const)"
      :key="side"
      type="button"
      class="nx-office-ruler__handle"
      :class="[`nx-office-ruler__handle--${side}`, { 'is-dragging': dragging === side }]"
      :style="{ left: `${side === 'left' ? left : width - right}px` }"
      :title="
        props.marginTitle?.(side, marginOf(side)) ?? `${marginLabel(side)}: ${marginOf(side)} mm`
      "
      :aria-label="marginLabel(side)"
      @pointerdown.prevent="beginDrag(side, $event)"
      @pointermove.prevent="moveDrag"
      @pointerup.prevent="endDrag"
      @pointercancel.prevent="endDrag"
      @keydown="onHandleKeydown(side, $event)"
    />
  </div>
</template>

<style scoped>
.nx-office-ruler {
  --nxt-office-ruler--centimetre: calc(var(--nxt-size-unit) * 2.375);
  --nxt-office-ruler--minor: calc(var(--nxt-office-ruler--centimetre) / 5);
  --nxt-office-ruler--origin: 0px;
  position: relative;
  box-sizing: border-box;
  height: calc(var(--nxt-size-unit) * 1.875);
  flex: 0 0 auto;
  overflow: visible;
  border: var(--nxt-hairline) solid var(--nxt-border-strong);
  border-radius: var(--nxt-radius-sm);
  background: var(--nxt-surface-bg);
  box-shadow: var(--nxt-shadow-sm);
  color: var(--nxt-fg-subtle);
  font-variant-numeric: tabular-nums;
  user-select: none;
}

.nx-office-ruler.is-sticky {
  position: sticky;
  top: 0;
  z-index: 2;
}

.nx-office-ruler__ticks,
.nx-office-ruler__ticks::after,
.nx-office-ruler__shade {
  position: absolute;
  pointer-events: none;
}

.nx-office-ruler__ticks {
  inset: 0;
  background-image: repeating-linear-gradient(
    to right,
    transparent 0 calc(var(--nxt-office-ruler--minor) - var(--nxt-hairline)),
    var(--nxt-border-strong) calc(var(--nxt-office-ruler--minor) - var(--nxt-hairline))
      var(--nxt-office-ruler--minor)
  );
  background-position: var(--nxt-office-ruler--origin) bottom;
  background-size: auto calc(var(--nxt-size-unit) * 0.375);
  background-repeat: repeat-x;
}

.nx-office-ruler__ticks::after {
  content: '';
  inset: 0;
  background-image: repeating-linear-gradient(
    to right,
    transparent 0 calc(var(--nxt-office-ruler--centimetre) - var(--nxt-hairline)),
    var(--nxt-fg-subtle) calc(var(--nxt-office-ruler--centimetre) - var(--nxt-hairline))
      var(--nxt-office-ruler--centimetre)
  );
  background-position: var(--nxt-office-ruler--origin) bottom;
  background-size: auto calc(var(--nxt-size-unit) * 0.625);
  background-repeat: repeat-x;
}

.nx-office-ruler__shade {
  top: 0;
  bottom: 0;
  background: var(--nxt-border-subtle);
}

.nx-office-ruler__shade--left {
  left: 0;
  border-right: var(--nxt-hairline) solid var(--nxt-border-strong);
  border-radius: var(--nxt-radius-sm) 0 0 var(--nxt-radius-sm);
}

.nx-office-ruler__shade--right {
  right: 0;
  border-left: var(--nxt-hairline) solid var(--nxt-border-strong);
  border-radius: 0 var(--nxt-radius-sm) var(--nxt-radius-sm) 0;
}

.nx-office-ruler__label {
  position: absolute;
  top: calc(var(--nxt-size-unit) * 0.1875);
  z-index: 1;
  transform: translateX(-50%);
  color: var(--nxt-fg-subtle);
  font-size: calc(var(--nxt-size-unit) * 0.5625);
  line-height: 1;
  pointer-events: none;
}

.nx-office-ruler__tab-kind {
  position: absolute;
  top: calc(var(--nxt-size-unit) * 0.1875);
  left: calc(var(--nxt-size-unit) * 0.1875);
  z-index: 4;
  width: calc(var(--nxt-size-unit) * 1.5625);
  height: calc(var(--nxt-size-unit) * 1.25);
  padding: 0 calc(var(--nxt-hairline) * 2);
  border: var(--nxt-hairline) solid var(--nxt-border-strong);
  border-radius: var(--nxt-radius-sm);
  background: var(--nxt-surface-bg);
  color: var(--nxt-brand-bg);
  font-family: var(--nxt-font);
  font-size: calc(var(--nxt-size-unit) * 0.625);
  font-weight: var(--nxt-font-weight-bold);
  line-height: 1;
}

.nx-office-ruler__tab-stop {
  position: absolute;
  top: calc(var(--nxt-size-unit) * 0.8125);
  z-index: 3;
  width: calc(var(--nxt-size-unit) * 1.0625);
  height: var(--nxt-size-unit);
  padding: 0;
  transform: translateX(-50%);
  border: 0;
  border-bottom: calc(var(--nxt-hairline) * 2) solid currentColor;
  border-radius: calc(var(--nxt-hairline) * 2);
  background: transparent;
  color: var(--nxt-brand-bg);
  font-family: var(--nxt-font);
  font-size: calc(var(--nxt-size-unit) * 0.5625);
  font-weight: var(--nxt-font-weight-bold);
  line-height: 1;
  cursor: pointer;
}

.nx-office-ruler__tab-stop:hover,
.nx-office-ruler__tab-stop:focus-visible {
  outline: var(--nxt-hairline) solid var(--nxt-brand-bg);
  outline-offset: var(--nxt-hairline);
  background: var(--nxt-brand-subtle-bg);
}

.nx-office-ruler__handle {
  position: absolute;
  top: calc(var(--nxt-size-unit) * -0.1875);
  z-index: 3;
  width: calc(var(--nxt-size-unit) * 1.125);
  height: calc(var(--nxt-size-unit) * 2.125);
  padding: 0;
  transform: translateX(-50%);
  border: 0;
  outline: none;
  background: transparent;
  color: var(--nxt-brand-bg);
  cursor: ew-resize;
  touch-action: none;
}

/* The handle's arrow on top and its line down to the ticks. */
.nx-office-ruler__handle::before {
  content: '';
  position: absolute;
  top: var(--nxt-hairline);
  left: calc(var(--nxt-size-unit) * 0.25);
  border-right: calc(var(--nxt-size-unit) * 0.3125) solid transparent;
  border-left: calc(var(--nxt-size-unit) * 0.3125) solid transparent;
  border-top: calc(var(--nxt-size-unit) * 0.4375) solid currentColor;
  filter: drop-shadow(0 var(--nxt-hairline) 0 var(--nxt-surface-bg));
}

.nx-office-ruler__handle::after {
  content: '';
  position: absolute;
  top: calc(var(--nxt-size-unit) * 0.5);
  bottom: calc(var(--nxt-size-unit) * 0.1875);
  left: calc(var(--nxt-size-unit) * 0.5);
  width: calc(var(--nxt-hairline) * 2);
  border-radius: var(--nxt-hairline);
  background: currentColor;
  opacity: 0.85;
}

.nx-office-ruler__handle:focus-visible::before {
  filter: drop-shadow(0 0 calc(var(--nxt-hairline) * 2) var(--nxt-surface-bg))
    drop-shadow(0 0 calc(var(--nxt-hairline) * 2) var(--nxt-brand-bg));
}

/* The vertical ruler: ticks on the page side, numbers on the outer side. */
.nx-office-ruler.is-vertical {
  width: calc(var(--nxt-size-unit) * 1.875);
  height: auto;
  overflow: hidden;
}

.nx-office-ruler.is-vertical .nx-office-ruler__ticks {
  background-image: repeating-linear-gradient(
    to bottom,
    transparent 0 calc(var(--nxt-office-ruler--minor) - var(--nxt-hairline)),
    var(--nxt-border-strong) calc(var(--nxt-office-ruler--minor) - var(--nxt-hairline))
      var(--nxt-office-ruler--minor)
  );
  background-position: right var(--nxt-office-ruler--origin);
  background-size: calc(var(--nxt-size-unit) * 0.375) auto;
  background-repeat: repeat-y;
}

.nx-office-ruler.is-vertical .nx-office-ruler__ticks::after {
  background-image: repeating-linear-gradient(
    to bottom,
    transparent 0 calc(var(--nxt-office-ruler--centimetre) - var(--nxt-hairline)),
    var(--nxt-fg-subtle) calc(var(--nxt-office-ruler--centimetre) - var(--nxt-hairline))
      var(--nxt-office-ruler--centimetre)
  );
  background-position: right var(--nxt-office-ruler--origin);
  background-size: calc(var(--nxt-size-unit) * 0.625) auto;
  background-repeat: repeat-y;
}

.nx-office-ruler.is-vertical .nx-office-ruler__shade {
  top: auto;
  right: 0;
  bottom: auto;
  left: 0;
}

.nx-office-ruler.is-vertical .nx-office-ruler__shade--top {
  top: 0;
  border-bottom: var(--nxt-hairline) solid var(--nxt-border-strong);
  border-radius: var(--nxt-radius-sm) var(--nxt-radius-sm) 0 0;
}

.nx-office-ruler.is-vertical .nx-office-ruler__shade--bottom {
  bottom: 0;
  border-top: var(--nxt-hairline) solid var(--nxt-border-strong);
  border-radius: 0 0 var(--nxt-radius-sm) var(--nxt-radius-sm);
}

.nx-office-ruler.is-vertical .nx-office-ruler__label {
  left: calc(var(--nxt-size-unit) * 0.1875);
  transform: translateY(-50%);
}

/* The shared document workspace: a flat, lower ruler. */
[data-office-view] .nx-office-ruler {
  height: calc(var(--nxt-size-unit) * 1.375);
  border-color: var(--nxt-border-subtle);
  box-shadow: none;
}

[data-office-view] .nx-office-ruler.is-vertical {
  width: calc(var(--nxt-size-unit) * 1.375);
  height: auto;
}

[data-office-view] .nx-office-ruler__tab-kind {
  top: var(--nxt-hairline);
  height: calc(var(--nxt-size-unit) * 1.125);
}

[data-office-view] .nx-office-ruler__handle {
  height: calc(var(--nxt-size-unit) * 1.75);
}

@media (forced-colors: active) {
  .nx-office-ruler__handle,
  .nx-office-ruler__tab-stop {
    color: ButtonText;
  }
}
</style>
